/*
 * Host-aware VoxPont document branding.
 *
 * The SPA's index.html is shared between HealthDesk and the VoxPont hosts, so we
 * cannot hardcode a VoxPont favicon / social image there without breaking
 * HealthDesk. Instead, while a VoxPont page is mounted on a VoxPont host we swap
 * the favicon, apple-touch-icon, and og/twitter social-image meta at runtime,
 * then restore the previous values on cleanup (e.g. SPA navigation back to a
 * HealthDesk route).
 *
 * Note: crawlers that don't execute JS won't see these runtime tags; full
 * crawler coverage would require a VoxPont-specific prerendered index.html.
 */

const BRAND_BASE = "/brand/voxpont";

const FAVICON_SVG = `${BRAND_BASE}/favicon.svg`;
const FAVICON_PNG = `${BRAND_BASE}/favicon-64.png`;
const APPLE_ICON = `${BRAND_BASE}/app-icon-180.png`;
const SOCIAL_IMAGE = `${BRAND_BASE}/linkedin-banner.png`;

/** Find or create a <link rel> in <head>; returns the element. */
function ensureLink(selector, create) {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = create();
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

/** Find or create a <meta> by name/property; returns the element + created flag. */
function ensureMeta(attr, value) {
  const selector = `meta[${attr}="${value}"]`;
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

/**
 * Apply VoxPont favicon + social meta. Returns a cleanup function that restores
 * the prior document head state.
 */
export function applyVoxPontBranding() {
  const restores = [];

  // --- Favicon (rel="icon"). Prefer the crisp SVG, with a PNG fallback link. ---
  const icon = ensureLink('link[rel="icon"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "icon");
    return l;
  });
  const prevIconHref = icon.el.getAttribute("href");
  const prevIconType = icon.el.getAttribute("type");
  icon.el.setAttribute("type", "image/svg+xml");
  icon.el.setAttribute("href", FAVICON_SVG);
  restores.push(() => {
    if (icon.created) {
      icon.el.remove();
    } else {
      if (prevIconHref != null) icon.el.setAttribute("href", prevIconHref);
      if (prevIconType != null) icon.el.setAttribute("type", prevIconType);
      else icon.el.removeAttribute("type");
    }
  });

  // PNG fallback for engines without SVG favicon support.
  const iconPng = ensureLink('link[rel="alternate icon"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "alternate icon");
    l.setAttribute("type", "image/png");
    return l;
  });
  const prevIconPngHref = iconPng.el.getAttribute("href");
  iconPng.el.setAttribute("href", FAVICON_PNG);
  restores.push(() => {
    if (iconPng.created) iconPng.el.remove();
    else if (prevIconPngHref != null) iconPng.el.setAttribute("href", prevIconPngHref);
  });

  // --- Apple touch icon ---
  const apple = ensureLink('link[rel="apple-touch-icon"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "apple-touch-icon");
    return l;
  });
  const prevAppleHref = apple.el.getAttribute("href");
  apple.el.setAttribute("href", APPLE_ICON);
  restores.push(() => {
    if (apple.created) apple.el.remove();
    else if (prevAppleHref != null) apple.el.setAttribute("href", prevAppleHref);
  });

  // --- Social meta (og + twitter) ---
  const socialUrl = `${window.location.origin}${SOCIAL_IMAGE}`;
  const metaPairs = [
    ["property", "og:image"],
    ["name", "twitter:image"],
    ["name", "twitter:card"],
  ];
  const metaValues = {
    "og:image": socialUrl,
    "twitter:image": socialUrl,
    "twitter:card": "summary_large_image",
  };
  for (const [attr, key] of metaPairs) {
    const { el, created } = ensureMeta(attr, key);
    const prevContent = el.getAttribute("content");
    el.setAttribute("content", metaValues[key]);
    restores.push(() => {
      if (created) el.remove();
      else if (prevContent != null) el.setAttribute("content", prevContent);
      else el.removeAttribute("content");
    });
  }

  return () => {
    // Restore in reverse so created-element removals are clean.
    for (let i = restores.length - 1; i >= 0; i -= 1) restores[i]();
  };
}
